import { useDebounceFn } from '@vueuse/core'

import axios from 'axios'

import { isNumber } from './utils'

import type { ActionSettings } from './types/marketingFlow'
import type { Store } from '@/views/contests/Components/types/global'

export function useMessageLengthCalculation() {
  const { t } = useI18n()
  const notification = useNotification()

  const messageLength = ref<number>(0)
  const variablesLength = ref<Record<string, number>>({})
  const isCheckingMessageLength = ref<boolean>(false)

  const getMessageLength = useDebounceFn(async (actionSettings: ActionSettings, stores: Store[]) => {
    const messageAction = actionSettings.actions.find((action) => ['SEND_SMS', 'SEND_PUSH'].includes(action.actionType))

    if (!messageAction?.messageContent?.length) {
      messageLength.value = 0
      isCheckingMessageLength.value = false
      return
    }

    try {
      const rewardAction = actionSettings.actions.find((action) => action.actionType === 'REWARD')
      const pointsAction = actionSettings.actions.find((action) => action.actionType === 'BONUS_POINTS')

      const { data } = await axios.get('/marketing-flows/message-length', {
        params: {
          message: messageAction.messageContent,
          storeIds: stores?.map((store) => store.id) || [],
          rewardId: rewardAction?.selectedReward,
          points: isNumber(pointsAction?.bonusPoints) ? Number(pointsAction.bonusPoints) : null,
          type: messageAction.actionType === 'SEND_PUSH' ? 'push' : 'sms'
        }
      })
      messageLength.value = data?.data?.messageLength ?? 0
      isCheckingMessageLength.value = false
    } catch {
      messageLength.value = 0
      isCheckingMessageLength.value = false
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
      })
    }
  }, 500)

  const getVariablesLength = async (actionSettings: ActionSettings, stores: Store[]) => {
    try {
      const messageAction = actionSettings.actions.find((action) =>
        ['SEND_SMS', 'SEND_PUSH'].includes(action.actionType)
      )
      const rewardAction = actionSettings.actions.find((action) => action.actionType === 'REWARD')
      const pointsAction = actionSettings.actions.find((action) => action.actionType === 'BONUS_POINTS')

      if (!messageAction) {
        return
      }

      const { data } = await axios.get('marketing-flows/variables-length', {
        params: {
          storeIds: stores?.map((store) => store.id) || [],
          rewardId: rewardAction?.selectedReward,
          points: isNumber(pointsAction?.bonusPoints) ? Number(pointsAction.bonusPoints) : null,
          type: messageAction.actionType === 'SEND_PUSH' ? 'push' : 'sms'
        }
      })
      variablesLength.value = data.data
    } catch {
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
      })
    }
  }

  const handleMessageLengthCalculation = (actionSettings: ActionSettings, stores: Store[]) => {
    isCheckingMessageLength.value = true
    messageLength.value = null
    getMessageLength(actionSettings, stores)
  }

  return {
    messageLength,
    variablesLength,
    isCheckingMessageLength,
    handleMessageLengthCalculation,
    getVariablesLength
  }
}
